import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ChevronDown, Globe, User, LayoutDashboard, LogOut, Shield } from "lucide-react";
import { useLocale } from "../../context/LocaleContext";
import { useAuth } from "../../context/AuthContext";
import { Mascot } from "../Mascot";
import { Logo } from "../Logo";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const EXPLORE = ["nutrition", "fitness", "recipes", "exercises", "body", "sports", "sleep", "weight"];

export function Header() {
  const { locale, sections, locales, changeLocale, t, lp, sp } = useLocale();
  const { user, logout, isStaff } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const byslug = Object.fromEntries(sections.map((s) => [s.slug, s]));
  const exploreItems = EXPLORE.map((slug) => byslug[slug]).filter(Boolean);
  const topLinks = [
    { slug: "goals", label: t("navGoals"), testid: "nav-goals" },
    { slug: "calculators", label: t("navTools"), testid: "nav-tools" },
    { slug: "articles", label: t("navLearn"), testid: "nav-learn" },
    { slug: "products", label: t("navProducts"), testid: "nav-products" },
  ];

  function submitSearch(e) {
    e.preventDefault();
    if (q.trim()) navigate(lp(`/search?q=${encodeURIComponent(q.trim())}`));
  }

  return (
    <header className="glass sticky top-0 z-50 shadow-sm">
      <div className="container-page flex h-16 items-center justify-between gap-3">
        <Link to={lp("")} className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <Logo className="h-7 sm:h-8" eager alt="LIVYVIO" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-explore" className="flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700">
                {t("explore")} <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[520px] p-3">
              <div className="grid grid-cols-2 gap-1">
                {exploreItems.map((s) => (
                  <DropdownMenuItem key={s.slug} asChild>
                    <Link to={lp(`/${s.path}`)} data-testid={`explore-${s.slug}`} className="cursor-pointer rounded-lg px-3 py-2">
                      <span className="font-medium text-slate-800">{s.title}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {topLinks.map((l) => (
            <Link key={l.slug} to={lp(`/${sp(l.slug)}`)} data-testid={l.testid}
              className="rounded-full px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
                className="h-9 w-36 rounded-full border border-slate-200 bg-white/80 pl-9 pr-3 text-sm outline-none transition-all focus:w-52 focus:ring-2 focus:ring-emerald-500/40" />
            </div>
          </form>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="locale-switcher" className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold uppercase text-slate-700 hover:bg-slate-50">
                <Globe className="h-4 w-4" /> {locale}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {locales.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`locale-${l.code}`} onClick={() => changeLocale(l.code)} className="cursor-pointer">
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white font-semibold">
                  {user.name?.[0]?.toUpperCase() || <User className="h-4 w-4" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user.email}</div>
                <DropdownMenuItem asChild><Link to={lp("/dashboard")} data-testid="menu-dashboard" className="cursor-pointer"><LayoutDashboard className="mr-2 h-4 w-4" />{t("dashboard")}</Link></DropdownMenuItem>
                {isStaff && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin" className="cursor-pointer"><Shield className="mr-2 h-4 w-4" />{t("admin")}</Link></DropdownMenuItem>}
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-red-600"><LogOut className="mr-2 h-4 w-4" />{t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="hidden sm:inline-flex rounded-full bg-emerald-600 hover:bg-emerald-700 transition-transform hover:scale-105 active:scale-95" data-testid="header-login">
              <Link to={lp("/login")}>{t("signIn")}</Link>
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden rounded-full p-2 hover:bg-slate-100" data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <SheetHeader><SheetTitle className="font-heading flex items-center gap-2"><Logo className="h-6" alt="LIVYVIO" /></SheetTitle></SheetHeader>
              <form onSubmit={(e) => { submitSearch(e); setOpen(false); }} className="mt-4">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="mobile-search-input" className="h-10 w-full rounded-full border border-slate-200 px-4 text-sm" />
              </form>
              <p className="mt-5 px-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("explore")}</p>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {exploreItems.map((s) => (
                  <Link key={s.slug} to={lp(`/${s.path}`)} onClick={() => setOpen(false)} data-testid={`mobile-nav-${s.slug}`} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50">{s.title}</Link>
                ))}
              </div>
              <div className="mt-3 flex flex-col border-t pt-3">
                {topLinks.map((l) => (
                  <Link key={l.slug} to={lp(`/${sp(l.slug)}`)} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50">{l.label}</Link>
                ))}
              </div>
              <div className="mt-3 border-t pt-3 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to={lp("/dashboard")} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-slate-50">{t("dashboard")}</Link>
                    {isStaff && <Link to="/admin" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-slate-50">{t("admin")}</Link>}
                    <button onClick={() => { logout(); setOpen(false); }} className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50">{t("logout")}</button>
                  </>
                ) : (
                  <>
                    <Link to={lp("/login")} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-slate-50">{t("signIn")}</Link>
                    <Link to={lp("/register")} onClick={() => setOpen(false)} className="rounded-full bg-emerald-600 px-3 py-2.5 text-center text-sm font-semibold text-white">{t("register")}</Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
